import { getPartnerResources } from '@entities/partner/api'
import { useUserStore } from '@entities/user'
import useMobileView from '@shared/hooks/isMobile'
import { getErrorMessage } from '@shared/lib/errors'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { produce } from 'immer'
import { useEffect, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import {
    createChannel,
    deleteChannel,
    editChannel,
    getChannel,
    getChannels,
    getEmployees,
    getTempChannel,
    updateInfo
} from '../../api'
import { useChannelStore } from '../store/channel'
import type { Channel } from '../types'

export const useChannels = (partnerId?: number) => {
    const queryClient = useQueryClient()

    const { currentChannel, setCurrentChannel } = useChannelStore(
        useShallow(({ current, setCurrent }) => ({ currentChannel: current, setCurrentChannel: setCurrent }))
    )

    const channelId = currentChannel?.id
    const queryKey = ['channels']
    const partnerChannelsQueryKey = ['channels', 'partner', partnerId]

    const channels = useQuery({ queryKey, queryFn: getChannels })

    const channelDetails = useQuery({
        queryKey: ['channelDetails', channelId],
        queryFn: () => getChannel(channelId),
        enabled: !!channelId
    })

    const currentChannelExists = useMemo(
        () => channels.data?.find((c) => c.id === currentChannel?.id),
        [channels.data, currentChannel?.id]
    )

    const currentPartnerResources = useQuery({
        queryKey: partnerChannelsQueryKey,
        queryFn: async () => {
            if (partnerId) return getPartnerResources({ partnerId })
        },
        enabled: !!partnerId
    })

    const isMobile = useMobileView()

    const tempChannel = useQuery({
        queryKey: ['temp-channel'],
        queryFn: () => getTempChannel(),
        enabled: isMobile
    })

    const { user } = useUserStore()

    const currentPermissions = useMemo(
        () => channelDetails.data?.settings?.employees.employees.find((e) => e.user.id === user?.id)?.permissions,
        [channelDetails.data?.settings?.employees.employees, user?.id]
    )

    useEffect(() => {
        if (channels.data?.length && !currentChannel) {
            setCurrentChannel(channels.data[0])
        }
    }, [channels.data, currentChannel, setCurrentChannel])

    const { message } = App.useApp()

    const createChannelMutation = useMutation({
        mutationFn: createChannel,
        onSuccess: (data) => {
            const previousChannels: Channel[] = queryClient.getQueryData(queryKey) ?? []
            const updatedChannels = [...previousChannels, data]
            queryClient.setQueryData(queryKey, updatedChannels)
            queryClient.refetchQueries({ queryKey: ['channels', 'partner', partnerId] })
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const getEmployeesMutation = useMutation({
        mutationFn: getEmployees,
        onSuccess: (data) => {
            // const link = document.createElement('a')
            // link.href = window.URL.createObjectURL(data)
            // link.download = 'table'
            // link.click()
            message.success('Данные отправлены в телеграм')
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const updateInfoMutation = useMutation({
        mutationFn: updateInfo,
        onSuccess: () => {
            message.success('Данные обновились')
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const editChannelMutation = useMutation({
        mutationFn: editChannel,
        onSuccess: (data) => {
            const previousChannels: Channel[] = queryClient.getQueryData(queryKey) ?? []
            const updatedChannels = produce(previousChannels, (draft) => {
                const index = draft.findIndex((i) => i.id === data.id)
                if (index !== -1) {
                    draft[index] = data
                }
            })
            queryClient.setQueryData(queryKey, updatedChannels)
            message.success('Данные сохранены')
        }
    })

    const deleteChannelMutation = useMutation({
        mutationFn: deleteChannel,
        onSuccess: (data, variables) => {
            const previousChannels: Channel[] = queryClient.getQueryData(queryKey) ?? []
            const updatedChannels = previousChannels.filter((item) => item.id !== variables.id)
            queryClient.setQueryData(queryKey, updatedChannels)
        }
    })

    return {
        channels: channels.data,
        channelDetails: channelDetails.data,
        tempChannel: tempChannel.data,
        partnerResources: currentPartnerResources.data,
        currentChannel: currentChannelExists ? currentChannel : null,
        currentChannelPermissions: currentPermissions,
        isChannelsLoading: channels.isPending,
        createChannelIsLoading: createChannelMutation.isPending,
        getEmployeesIsLoading: getEmployeesMutation.isPending,
        deleteChannelIsLoading: deleteChannelMutation.isPending,
        editChannelIsLoading: editChannelMutation.isPending,
        actions: {
            refetchPartnerResources: currentPartnerResources.refetch,
            refetchTempChannel: tempChannel.refetch,
            getEmployees: getEmployeesMutation.mutateAsync,
            updateInfo: updateInfoMutation.mutateAsync,
            createChannel: createChannelMutation.mutateAsync,
            deleteChannel: deleteChannelMutation.mutateAsync,
            editChannel: editChannelMutation.mutateAsync,
            setCurrentChannel
        }
    }
}
